// @flow
import React, { useEffect } from 'react';
import LazyLoad from 'react-lazyload';
import { withRouter } from 'react-router-dom';
import { Container, Row, Col, Jumbotron } from 'reactstrap';
import { Head, GuideCard, WhitePanelLayout, JumbotronImageSlider, ImageUploader } from '@dom/components';
import { connect } from 'react-redux';
import Map from 'lodash/map';
// import isEmpty from 'lodash/isEmpty';
import config from '@dom/config';
import { isSuperUser } from '@dom/utils';

import SearchForm from './SearchForm';
import ModifySelectionForm from './ModifySelectionForm';
import BannerInfo from './BannerInfo';
import SelectGuidePanel from './SelectGuidePanel';
import UpcomingTreks from './UpcomingTreks';
import { ReviewsRatings, CardsSlider } from '../../components';

import {
  sortGuidesByRatings,
  sortGuidesByDailyWage,
  filterOutDailyWage,
  filterOutPorterGuides,
} from '../../utils';
import { useSelector } from '../../redux';
import { getPlatformLink } from './utils';
import fetchGuideData from '../../api/fetchGuideData';
import fetchGuideAlbum from '../../api/fetchGuideAlbum';

import { getGuideDataById } from '../../redux/selectors';

import './profile.scss';

const RATINGS_TITLES = [
  'Communication',
  'Knowledge',
  'Organization Skills',
  'Fun Factor',
  'Safety',
];

const platformIcons = {
  facebook: 'icon icon-hg-facebook-square',
  instagram: 'icon icon-hg-instagram-1',
  youtube: 'icon icon-hg-youtube-logo',
  whatsapp: 'icon icon-hg-whatsapp',
  viber: 'icon icon-hg-viber',
  website: 'icon icon-hg-internet',
}

function Telephone(number, index, array) {
  return <a href={`tel:${number}`}>{number}{index !== array.length - 1 ? ', ' : ''}</a>;
}

function InfoItem(val, idx, ) {
  return (
    <span key={val + idx}>
      {idx > 0 ? ', ' : ''}
      {val}
    </span>
  );
}

type InfoRowProps = {
  value?: string,
  children?: React.ReactChildren,

  title: string,
};

function InfoRow(props: InfoRowProps) {
  const { children, title, value } = props;

  // if the value is not present, we might as well not show the content
  if (!children && !value) return null;

  return (
    <div className="flex-rows">
      <div className="key">{title}</div>
      <div className="value">{value || children}</div>
    </div>
  );
}

function joinByComma(arr) {
  if (!arr || arr.length === 0) return null;

  return arr.join(', ');
}

function Profile(props) {
  const { 
    match,
    isViewingOwnProfile,
    numberOfReviews,
    ratings,
  } = props;
  const { id } = match.params;

  const {
    fullname,
    about,
    originallyFrom,
    livesIn,
    expertiseRegions,
    previousTreks,
    emailAddress,
    phoneNumbers,
    dailyWages,
    safetyTrainings,
    experienceYears,
    languages,
    specialization,
    upcomingTreks,
    chatPlatformLinks,
    album,
    profileImage,
    // bannerImages,
  } = useSelector(getGuideDataById(id)) || {};

  const { amount, currency } = dailyWages || {};

  useEffect(() => {
    async function fetchData() {
      const guide = await fetchGuideData(id);
      fetchGuideAlbum(id, guide);
    };
    fetchData();
  }, [id]);

  const bannerInfoData = {
    id,
    name: fullname,
    livesIn,
    experienceYears,
    currency,
    amount,
    ratings,
    numberOfReviews,
    upcomingTreks,
    chatPlatformLinks,
  };
  
  const images = album
    ? album.map(img => `https://honeyguideapps.s3.amazonaws.com/static/${img.primaryImage}`)
    : [`${config.RESOURCE_URL}/${profileImage && profileImage}`];

  const isSuperUserViewingProfile = isSuperUser() ;

  return (
    <Head title={`${fullname}`}>
      <Container>
        <Row>
          <Col lg="3" xs="12" className="sidebar">
            <SearchForm />
            <WhitePanelLayout className="d-none d-lg-block">
              <p style={{ marginBottom: '0' }}>
                <p style={{ marginBottom: '4px' }}>Before booking, feel free to confirm if the guide is the right one for you by:</p>
                <ol style={{ marginBottom: '0', listStyle: 'decimal', marginLeft: '15px' }}>
                  <li style={{ marginBottom: '4px'  }}>Chatting with her/him using the contact information provided.</li>
                  <li>Going through their Social Media Link provided.</li>
                </ol>
              </p>
            </WhitePanelLayout>
          </Col>
          <Col lg="9" xs="12">
            <BannerInfo data={bannerInfoData} />
            <Jumbotron className="image-slider">
              <ImageUploader
                uploadUrl={`${config.ENDPOINT}/users/${id}/cover_image`}
                role={isSuperUserViewingProfile || isViewingOwnProfile}
                pageContext="userProfile"
                multiple={true}
              >
                {(uploadedImages) => (
                  <JumbotronImageSlider
                    showNav
                    images={images}
                    uploadedUrls={uploadedImages}
                  // alt={`${name}, ${region}`}
                  />
                )}
              </ImageUploader>
            </Jumbotron>
            <WhitePanelLayout>
              <div className="flex-column">
                <InfoRow
                  title="Introduction"
                  value={about}
                />
                <InfoRow title="From" value={originallyFrom} />
                <InfoRow
                  title="Preferred Trekking Regions"
                  value={joinByComma(expertiseRegions)}
                />
                <InfoRow
                  title="Previous Treks"
                  value={joinByComma(previousTreks)}
                />
              </div>
            </WhitePanelLayout>
            <WhitePanelLayout title="Contact Information">
              <InfoRow title="Email Address">
                {emailAddress}
              </InfoRow>
              <InfoRow title="Contact Number">
                {Map(phoneNumbers, Telephone)}
              </InfoRow>
              <InfoRow title="Chat Platform Links">
                <ul className="profile-chat-links">
                  {chatPlatformLinks && chatPlatformLinks.map(({ platform, link }) => {
                    if (!link) return null;
                    return (
                      <li key={platform}>
                        <a
                          href={getPlatformLink(link, platform)}
                          className={platform}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                        >
                          <i className={platformIcons[platform]} />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </InfoRow>
            </WhitePanelLayout>
            <WhitePanelLayout title="Professional Information">
              <div className="flex-column">
                <InfoRow title="Daily Wages (USD per day)">
                  {currency}{' '}
                  {amount}
                </InfoRow>
                <InfoRow title="Experience In Year">
                  {experienceYears}
                </InfoRow>
                <InfoRow title="Safety Training">
                  {Map(safetyTrainings, InfoItem)}
                </InfoRow>
                <InfoRow title="Languages">
                  {Map(languages, InfoItem)}
                </InfoRow>
                <InfoRow title="Specialization">
                  {Map(specialization, InfoItem)}
                </InfoRow>
                <InfoRow title="Expertise Region">
                  {Map(expertiseRegions, InfoItem)}
                </InfoRow>
              </div>
            </WhitePanelLayout>
            <ModifySelectionForm data={bannerInfoData}/>
            <SelectGuidePanel data={bannerInfoData}/>
            <UpcomingTreks upcomingTreks={upcomingTreks} />
            <ReviewsRatings ratingsTitles={RATINGS_TITLES} origin="profile" />

            <LazyLoad offset={100}>
              <CardsSlider
                title="See Also"
                seeAllLink="/list"
                showNoOfCards={4}
                endpoint="users/leaders?is_featured=1"
                Card={GuideCard}
                sortFunctions={[sortGuidesByRatings, sortGuidesByDailyWage]}
                filterFunctions={[filterOutPorterGuides, filterOutDailyWage]}
              />
            </LazyLoad>
          </Col>
        </Row>
      </Container>
    </Head>
  );
}


const mapStateToProps = ({ user, ratings, reviews }, { match }) => {
  const { id } = match.params;
  const userId = user.data && user.data.id;

  return {
    isViewingOwnProfile: id === userId,
    ratings: ratings.ratings,
    hasStarRatings: Object.keys(ratings.ratings || {}).length > 0,
    numberOfReviews: reviews.reviews.length,
  }; 
}
const connectedProfile = connect(mapStateToProps)(Profile);

export default withRouter(connectedProfile);
